<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Taxonomy } from '@/types/filters';
import type { Location, RatingData } from '@/types/products';

import useBookingOpening from '@/composables/useBookingOpening';
import useModeDetails from '@/composables/useModeDetails';
import { useDetailsStore } from '@/stores/useDetailsStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { buildGA4ItemFromProduct, pushGA4EcommerceEvent } from '@/utils/gtmEcommerce';
import { formatDisplayRating, isEcoFriendly, pushContext, pushEventFromPostMessage } from '@/utils/utils.js';

import BookingWidget from '../booking/BookingWidget.vue';
import Carousel from '../carousel/Carousel.vue';
import TheCard from '../mode-list/TheCard.vue';
import BookingOpeningMessage from './BookingOpeningMessage.vue';
import DesktopHeader from './DesktopHeader.vue';
import EcoFriendly from './EcoFriendly.vue';
import ExtraContent from './ExtraContent.vue';
import GoogleRatingReviews from './GoogleRatingReviews.vue';
import MapWidget from './MapWidget.vue';
import MobileHeader from './MobileHeader.vue';
import ViewMoreContent from './ViewMoreContent.vue';
import YoutubeContent from './YoutubeContent.vue';

const props = defineProps<{
  productId: string;
  noWidgetTaxoId?: string | null;
  prefixUrl?: string | null;
}>();

const { t } = useI18n();

const detailsStore = useDetailsStore();
const globalConfigurationStore = useGlobalConfigurationStore();
const { currentLocale, globalConfiguration } = storeToRefs(globalConfigurationStore);

const {
  productDetails,
  crossSellingActivities,
} = storeToRefs(detailsStore);

const {
  fetchCrossSellingActivities,
  fetchActivityDetails,
  fetchDistributor,
  getProviderWidgetUrl,
} = detailsStore;

const { fairguestWidgetId } = storeToRefs(detailsStore);

const {
  generateOpenGraphTags,
  setupJsonLd,
  scrollToFairguestWidget,
  scrollToWidget,
  loadFavoriteState,
  dispatchFavoriteEvent,
} = useModeDetails();

const { isBookingOpened } = useBookingOpening();

// TEMPLATE REFS
const mobileCTA = ref();
const bookingWidgetRef = ref();
const v2WidgetContainer = ref<HTMLElement>();
const mobileCTAObserver = ref<IntersectionObserver>();
const isMobileCTAHidden = ref(false);

const apiURL = ref<string>('');
const componentsBaseUrl = ref<string>('https://components.youbook.travel');
const abortController = ref<AbortController>(new AbortController());
const isError = ref<boolean>(false);
const youtubeVideos = ref<any[]>([]);
const iFrameProviderWidgetHeight = ref<number>();

const hasActivityRatings = ref<boolean | null>(false);
const rating = ref<number | null>(null);
const ratingReviewCount = ref<number | null>(null);
const ratingSource = ref<string>('');
const displayedRating = computed(() => formatDisplayRating(rating.value ?? 0));
const ratingsPOJO = ref<RatingData | null>(null);

const nextSession = computed(() => productDetails.value?.properties.find((it: any) => it.propertyName === 'nextSessionDate')?.value ?? null);
const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);
const localizedTitle = computed(() => productDetails.value?.names?.find((it: any) => it.lang === currentLocale.value)?.value ?? productDetails.value?.name);
const location = computed(() => productDetails.value?.meetingPoint?.location);
const documentations = computed(() => productDetails.value?.pdfs ?? []);

const desktopWidth = ref(window.innerWidth);
const desktopViewport = computed(() => desktopWidth.value >= 768);
const showFavorites = computed(() => globalConfiguration.value?.options?.hasFavorites !== false);

const FFV_APIKEY = 'YoubookFFVyBGWEQKbWKTJwTArIg9YcAqVEXaSohwM6LZ4FPREkHZg3BWKAB9hm0';
const isFFVDistributor = computed(() => globalConfiguration.value?.apikey === FFV_APIKEY);

const isBookableFFV = computed(() => {
  const internalsItems = productDetails.value?.properties
    .find((it: any) => it.propertyName === 'productEnrichmentByDistributor')
    ?.value
    ?.taxonomies
    ?.internals
    ?.items;
  return internalsItems ? internalsItems.some((internal: Taxonomy) => internal.id === '7d419dd9-6d98-45dd-8146-b25c8a1995f2') : false;
}); // Vente en ligne

const isV2Product = computed(() => productDetails.value?.properties
  .find((it: any) => it.propertyName === 'productEnrichmentByDistributor')
  ?.value
  ?.youbook_v2_activated === true);

const shouldDisplayWarning = computed(() => productDetails.value?.properties
  .find((it: any) => it.propertyName === 'productEnrichmentByDistributor')
  ?.value
  ?.distributor_id === 'Z23PA3Pp4S68jCDmjIFA');

const CTALabel = computed(() => {
  if (!isFFVDistributor.value)
    return t('cta.book_a_date');
  return isBookableFFV.value ? t('cta.book') : t('cta.learn_more');
});

const mobileCTALabel = computed(() => {
  if (!isFFVDistributor.value)
    return t('cta.book');
  return isBookableFFV.value ? t('cta.book') : t('cta.learn_more');
});

// CONTENT FROM ACTIVITY
const sourceDisplayConfig = computed(() => {
  const source = productDetails.value?.source;
  return {
    showProviderLogo: !isV2Product.value && source !== 'tamtam' && source !== 'ingenie'
        && !(isFFVDistributor.value && source === 'yoplanning'),
    hideProviderLogoInWidget: source === 'welogin' || source === 'ingenie',
  };
});

const hasWidget = computed(() => {
  if (!productDetails.value?.hasWidget)
    return false;
  if (!props.noWidgetTaxoId)
    return true;
  return !productDetails.value?.taxonomies?.some(
    (taxo: any) =>
      taxo.slug === 'regiondo'
          && taxo.children.some((child: any) => child?.id === props.noWidgetTaxoId),
  );
});
const isEcoFriendlyProduct = computed(() => isEcoFriendly(productDetails.value));
const supplierName = computed(() => productDetails.value?.properties.find((it: any) => it.propertyName === 'supplier')?.value?.name ?? 'N/A');
const spokenLanguages = computed(() => productDetails.value?.spokenLanguages);
const crossSellingProductIds = computed(() => productDetails.value?.properties.find((it: any) => it.propertyName === 'productEnrichmentByDistributor').value.cross_sellable_with ?? []);
const displayedPrice = computed(() => productDetails.value?.displayedPrice ?? null);

// HEADER DATA
const headerData = computed(() => {
  return {
    images: productDetails.value?.images,
    title: localizedTitle.value,
    source: productDetails.value?.source[0].toUpperCase() + productDetails.value?.source.substring(1),
    hasWidget: productDetails.value?.hasWidget,
    rating: hasActivityRatings.value
      ? {
          rating: displayedRating.value,
          ratingReviewCount: ratingReviewCount.value ?? 0,
          ratingSource: ratingSource.value,
        }
      : null,
  };
});

// MAP WIDGET DATA
const mapWidgetData = computed(() => {
  return {
    svgUrl: componentsBaseUrl.value,
    activityTitle: productDetails.value?.name,
    city: productDetails.value?.meetingPoint?.location?.locality,
    streetAddress: productDetails.value?.meetingPoint?.location?.streetAddress,
    howToGo: productDetails.value?.meetingPoint?.description ?? '',
    transport: productDetails.value?.meetingPoint?.nearestPublicTransport ?? '',
    gps: productDetails.value?.meetingPoint?.location?.gps as Location['gps'] ?? null,
    price: `${productDetails.value?.displayedPrice.priceFrom}`,
  };
});

// DESCRIPTIONS ALWAYS DISPLAYED
const languageDescriptions = computed(() => productDetails.value?.descriptions?.filter((it: any) => it.lang === currentLocale.value));
const presentationContent = computed(() => languageDescriptions.value?.find((it: any) => it.type === 'PRESENTATION')?.content ?? '');
const highlightContent = computed((): Array<string> => languageDescriptions.value?.filter((it: any) => it.type === 'HIGHLIGHT')
  .map((it: any) => it.content),
);
const hasHighlightContent = computed(() => highlightContent.value?.length > 0);

// YOUTUBE WIDGET
async function setupYouTubeVideos(videos: any) {
  try {
    const localeVideos = videos.filter((video: any) => video.lang === currentLocale.value);
    const promises = localeVideos.map(async (video: any) => {
      const videoId = new URL(video.url).pathname.split('/').pop();
      const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;

      const response = await fetch(oEmbedUrl);
      if (!response.ok)
        throw new Error('Error fetching metadata');

      const metadata = await response.json();
      return { ...video, metadata };
    });

    youtubeVideos.value = await Promise.all(promises);
    return youtubeVideos.value;
  }
  catch (error) {
    console.error('Error fetching oEmbed:', error);
  }
}

// FAIRGUEST & PROVIDER WIDGETS UTILS
function setupMobileCTAObserver(element: Element) {
  mobileCTAObserver.value?.disconnect();
  const options = {
    root: null,
    rootMargin: '10px',
    threshold: 0.5,
  };
  mobileCTAObserver.value = new IntersectionObserver(handleIntersect, options);
  mobileCTAObserver.value.observe(element);
}

function displayProviderWidget() {
  if (productDetails.value === null || !productDetails.value?.sourceId || !hasWidget.value) {
    return;
  }
  const component = document.querySelector('yb-details');
  const iframe = component?.shadowRoot?.getElementById('widget_wrapper');
  if (sourceDisplayConfig.value.hideProviderLogoInWidget) {
    const providerLogo = component?.shadowRoot?.querySelector('#cta_scroll_target p') as HTMLElement;

    if (providerLogo)
      providerLogo.style.display = 'none';
  }
  const url = getProviderWidgetUrl(currentLocale.value);
  iframe?.setAttribute('src', url);

  if (iframe) {
    setupMobileCTAObserver(iframe);
  }
}

function handleCTA(event: Event) {
  const url = productDetails.value?.bookingUrls.find((it: any) => it.lang === currentLocale.value)?.value ?? productDetails.value?.bookingUrl;
  hasWidget.value ? scrollToWidget(event) : window.open(url, '_blank');
}

function handleIntersect(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry: IntersectionObserverEntry) => {
    isMobileCTAHidden.value = entry.isIntersecting;
  });
}

function displayFairguestWidget() {
  const id = fairguestWidgetId.value ?? '1478';
  const component = document.querySelector('yb-details');
  const wrapper = component?.shadowRoot?.getElementById('fairguest_wrapper') as HTMLElement;
  wrapper.style.display = 'block';
  const htmlElement = document.createElement('rc-fairguest-widget-destination');
  htmlElement.setAttribute('id', id);
  if (isFFVDistributor.value) {
    const supplierId = productDetails.value?.properties.find((prop: any) => prop.propertyName === 'supplier')?.value.id;
    if (supplierId)
      htmlElement.setAttribute('sheet', `yb_${supplierId}`);
  }
  else {
    htmlElement.setAttribute('sheet', `yb_${productDetails.value?.id}`);
  }
  htmlElement.setAttribute('lang', currentLocale.value);
  htmlElement.setAttribute('type', 'default');
  const htmlScriptElement = document.createElement('script');
  htmlScriptElement.setAttribute('type', 'module');
  htmlScriptElement.textContent = `(async () => (await import(\'https://www.fairguest.com/api/v2/fairguest/widgets/${id}/js\')).load())();`;

  htmlElement.appendChild(htmlScriptElement);
  if (wrapper)
    wrapper.appendChild(htmlElement);
}

// POPOVERS CONTENT
const shareLinks = computed(() => [
  {
    name: 'Via Email',
    href: `mailto:?body=${window.location.origin + window.location.pathname}`,
    imgSrc: `${componentsBaseUrl.value}/icons-mail.png`,
  },
  {
    name: 'WhatsApp',
    href: `whatsapp://send?text=${window.location.href}`,
    imgSrc: `${componentsBaseUrl.value}/icons-whatsapp.png`,
  },
  {
    name: 'Facebook',
    href: `https://facebook.com/sharer/sharer.php?u=${window.location.href}`,
    imgSrc: `${componentsBaseUrl.value}/icons-facebook.png`,
  },
  {
    name: 'X/Twitter',
    href: `https://twitter.com/intent/tweet/?url=${window.location.href}`,
    imgSrc: `${componentsBaseUrl.value}/icons-x.png`,
  },
  {
    name: 'Linkedin',
    href: `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}`,
    imgSrc: `${componentsBaseUrl.value}/icons-linkedin.png`,
  },
  {
    name: 'Pinterest',
    href: `https://pinterest.com/pin/create/button/?url=${window.location.href}`,
    imgSrc: `${componentsBaseUrl.value}/icons-pinterest.png`,
  },
]);

const navigationLinks = computed(() => [
  {
    name: 'Google Maps',
    href: `https://www.google.com/maps/dir/?api=1&destination=${location.value?.gps?.lat},${location.value?.gps?.lon}`,
    imgSrc: `${componentsBaseUrl.value}/icons-gmaps.png`,
  },
  {
    name: 'Apple Plans',
    href: `http://maps.apple.com/?q=${location.value?.gps.lat},${location.value?.gps?.lon}`,
    imgSrc: `${componentsBaseUrl.value}/icons-applemaps.png`,
  },
  {
    name: 'Waze',
    href: `https://www.waze.com/ul?ll=${location.value?.gps.lat}%2C${location.value?.gps?.lon}&navigate=yes&zoom=17`,
    imgSrc: `${componentsBaseUrl.value}/icons-waze.png`,
  },
  {
    name: 'Mappy',
    href: `https://fr.mappy.com/itineraire#/voiture/48.8566,2.3522/${location.value?.gps?.lat},${location.value?.gps?.lon}/car/feuille_de_route/8`,
    imgSrc: `${componentsBaseUrl.value}/icons-mappy.png`,
  },
  {
    name: 'BlablaCar',
    href: `https://www.blablacar.fr/search?tn=${location.value?.locality}%2C%20France`,
    imgSrc: `${componentsBaseUrl.value}/icons-blablacar.png`,
  },
]);

// UTILS
function handleFairguestRating(e: any) {
  hasActivityRatings.value = e?.detail?.hasFairguestRating;
  rating.value = e?.detail?.fairguestRating;
  ratingReviewCount.value = e?.detail?.reviewCountForFairguestRating;
  ratingSource.value = 'FAIRGUEST';
}

function handleExternalRating() {
  const ratingProperty = productDetails.value?.properties.find((it: any) => it.propertyName === 'rating');
  if (ratingProperty && ratingProperty.value.rating) {
    hasActivityRatings.value = true;
    rating.value = ratingProperty.value.rating;
    ratingReviewCount.value = ratingProperty.value.ratingReviewCount;
    ratingSource.value = ratingProperty.value.source;
  }
}

function getProviderLogo(source: string) {
  return `${componentsBaseUrl.value}/${source}.png`;
}

function forcePageRefresh() {
  window.location.reload();
}

function resizeIframe(event: MessageEvent) {
  if (event.data?.type !== 'widget-resize') {
    return;
  }
  iFrameProviderWidgetHeight.value = event.data.widgetHeight + 20;
}

function handleResize() {
  desktopWidth.value = window.innerWidth;
}

// WATCHERS AND LIFECYCLE
watch(
  () => globalConfiguration.value?.apikey,
  () => {
    if (globalConfiguration.value && globalConfiguration.value?.apikey.length < 12)
      throw new Error('Invalid API key');
  },
);

watch(
  crossSellingProductIds,
  async () => {
    await fetchCrossSellingActivities(crossSellingProductIds.value);
  },
);

watch(v2WidgetContainer, (container) => {
  if (container) {
    setupMobileCTAObserver(container);
  }
});

onMounted(async () => {
  window.addEventListener('message', resizeIframe);
  window.addEventListener('message', pushEventFromPostMessage);
  window.addEventListener('fairguest:widget:loaded', handleFairguestRating);
  window.addEventListener('resize', handleResize);

  await Promise.all([
    await fetchDistributor(),
    await fetchActivityDetails(props.productId),
  ]);

  if (productDetails.value === null) {
    isError.value = true;
    return;
  }
  else {
    isError.value = false;
  }

  generateOpenGraphTags(productDetails.value, localizedTitle.value);
  setupJsonLd(productDetails.value, supplierName.value);

  const eventProduct = {
    id: productDetails.value?.id,
    name: productDetails.value?.name,
    location: productDetails.value?.meetingPoint?.location,
    priceFrom: productDetails.value?.priceFrom,
    originalPrice: productDetails.value?.originalPrice,
  };

  // Antoine - requestIdleCallback non compatible avec Safari (https://caniuse.com/requestidlecallback)
  // requestIdleCallback(async () => {
  pushContext({ event: 'detail_init', data: { product: eventProduct } });

  pushGA4EcommerceEvent('view_item', {
    currency: productDetails.value.currency,
    value: productDetails.value.priceFrom,
    items: [buildGA4ItemFromProduct(productDetails.value)],
  });

  loadFavoriteState();
  displayProviderWidget();

  const ratingProperty = productDetails.value?.properties.find((it: any) => it.propertyName === 'rating');
  if (fairguestWidgetId.value) {
    displayFairguestWidget();
  }
  else if (ratingProperty && ratingProperty.value?.source !== 'FAIRGUEST') {
    ratingsPOJO.value = ratingProperty.value;
    handleExternalRating();
  }

  if (productDetails.value?.youtubeVideos.length) {
    await setupYouTubeVideos(productDetails.value?.youtubeVideos);
  }
  // });
});

onBeforeUnmount(() => {
  mobileCTAObserver.value?.disconnect();
  window.removeEventListener('message', resizeIframe);
  window.removeEventListener('message', pushEventFromPostMessage);
  window.removeEventListener('fairguest:widget:loaded', handleFairguestRating);
  window.removeEventListener('resize', handleResize);

  abortController.value.abort();
});
</script>

<template>
  <link rel="preconnect" :href="apiURL" crossorigin="true">
  <link rel="preconnect" href="https://api.cloudly.space">

  <section
    class="details_component yb-preset" :style="[isError ? 'display:none;' : 'display:block;']"
    :aria-label="t('aria.details')"
  >
    <MobileHeader
      v-if="productDetails"
      :share-links="shareLinks" :mobile-header-data="headerData"
      @scroll-to-fairguest-widget="scrollToFairguestWidget" @toggle-favorite="dispatchFavoriteEvent(productDetails ?? undefined)"
    />
    <section id="mobile_cta" ref="mobileCTA" class="mobile_cta" :class="{ hidden: isMobileCTAHidden }" :aria-label="t('aria.cta')">
      <div class="mobile_wrapper">
        <div>
          <p class="text_content">
            {{ t('cta.price') }}
          </p>
          <p v-if="displayedPrice" class="price_wrapper">
            <span
              :style="[displayedPrice.hasDiscount ? 'text-decoration: line-through; font-weight: var(--yb-text-light); color: var(--yb-color-black)' : 'color: var(--yb-color-black);']"
            >
              {{ displayedPrice.originalPrice }}
            </span>&nbsp;
            <span v-if="displayedPrice.hasDiscount" class="price_from">{{ displayedPrice.priceFrom }}</span>
          </p>
        </div>
        <button class="cta_action" @click="handleCTA">
          {{ mobileCTALabel }}
        </button>
      </div>
    </section>

    <DesktopHeader
      v-if="productDetails"
      :share-links="shareLinks" :desktop-header-data="headerData"
      @scroll-to-fairguest-widget="scrollToFairguestWidget" @toggle-favorite="dispatchFavoriteEvent(productDetails ?? undefined)"
    />

    <div class="main_container">
      <div class="side_content">
        <section class="desktop_cta small_spacing sticky" :aria-label="t('aria.cta')">
          <div>
            <p class="text_content">
              {{ t('cta.price') }}
            </p>
            <p v-if="displayedPrice" class="desktop_price_wrapper">
              <span
                :style="[displayedPrice.hasDiscount ? 'text-decoration:line-through;font-weight:var(--yb-text-light);color:var(--yb-text-black);' : 'color:var(--yb-color-black);']"
              >
                {{ displayedPrice.originalPrice }}
              </span>&nbsp;
              <span v-if="displayedPrice.hasDiscount" class="desktop_price_from">{{ displayedPrice.priceFrom }}</span>
            </p>
          </div>

          <div>
            <button class="cta_action" :aria-label="t('aria.cta_action')" @click="handleCTA">
              {{ CTALabel }}
            </button>
          </div>

          <div v-if="sourceDisplayConfig.showProviderLogo" class="provider">
            <p class="text_content_small provider_content">
              {{ t('content.book_peacefully') }}
            </p>
            <img class="provider_img" :src="getProviderLogo(productDetails?.source)" :alt="t('aria.provider_logo')">
          </div>
        </section>
      </div>

      <div class="main_content">
        <section class="text_content normal_spacing" :aria-label="t('aria.presentation')">
          <ViewMoreContent :html-content="presentationContent" />
        </section>

        <section
          class="normal_spacing" :style="[hasHighlightContent ? 'display:block;' : 'display:none;']"
          :aria-label="t('aria.highlights')"
        >
          <div class="highlights_wrapper">
            <h3 class="text_content_bold">
              {{ t('content.highlights') }}
            </h3>
            <ViewMoreContent :html-content="highlightContent" />
          </div>
        </section>

        <section
          id="cta_scroll_target" class="normal_spacing"
          :style="[!hasWidget ? 'display:none;' : 'display:block;']"
          :aria-label="t('aria.provider_book')"
        >
          <h2 class="h2_title" style="margin-bottom:0.5rem;">
            {{ t('content.options') }}
          </h2>
          <h3 class="h3_title small_spacing">
            <span aria-hidden="true" style="margin-right:calc(var(--yb-radius-sm)/2);">
              <svg
                width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg"
                style="vertical-align:-3px;"
              >
                <path
                  d="M8.42615 3.49689L8.981 4.8471M11.7553 11.5981L12.3101 12.9483M10.0907 7.54751L10.6456 8.89772M2.10971 9.249C2.64685 9.02827 3.24969 9.02995 3.78559 9.25369C4.32149 9.47743 4.74657 9.90489 4.9673 10.442C5.18804 10.9792 5.18635 11.582 4.96261 12.1179C4.73888 12.6538 4.31142 13.0789 3.77427 13.2996L4.32912 14.6498C4.47628 15.0079 4.75966 15.2929 5.11693 15.4421C5.4742 15.5912 5.87609 15.5923 6.23419 15.4452L17.0359 11.0064C17.394 10.8592 17.6789 10.5758 17.8281 10.2186C17.9772 9.86128 17.9784 9.45939 17.8312 9.1013L17.2764 7.75109C16.7392 7.97182 16.1364 7.97014 15.6005 7.7464C15.0646 7.52266 14.6395 7.0952 14.4188 6.55806C14.198 6.02091 14.1997 5.41808 14.4235 4.88217C14.6472 4.34627 15.0746 3.9212 15.6118 3.70046L15.0569 2.35025C14.9098 1.99215 14.6264 1.70718 14.2691 1.55802C13.9119 1.40887 13.51 1.40774 13.1519 1.5549L2.35021 5.99373C1.99211 6.14089 1.70714 6.42427 1.55798 6.78154C1.40882 7.13881 1.4077 7.54069 1.55485 7.89879L2.10971 9.249Z"
                  stroke="#252525" stroke-width="1.75172" stroke-linecap="round" stroke-linejoin="round"
                />
              </svg>
            </span>
            {{ t('content.fee') }}
          </h3>
          <p v-if="shouldDisplayWarning" class="small_spacing warning">
            <span>{{ t('warning.important') }}</span>
            {{ t('warning.message') }}
          </p>
          <BookingOpeningMessage v-if="!isBookingOpened" class="small_spacing" />
          <!-- v-show (not v-if) so the provider iframe is still initialised and shows up as soon as bookings open -->
          <div v-if="isBookingOpened">
            <div v-if="isV2Product" ref="v2WidgetContainer">
              <BookingWidget ref="bookingWidgetRef" :activity-details="productDetails" />
            </div>
            <template v-else>
              <iframe id="widget_wrapper" src="" :height="iFrameProviderWidgetHeight" title="widget de vente" />
              <p v-if="sourceDisplayConfig.showProviderLogo" class="text_content_small provider_wrapper">
                {{ t('content.boooking_system_by') }}<span><img
                  id="provider_pic" height="25" width="100"
                  :src="getProviderLogo(productDetails?.source)"
                  :alt="t('aria.provider_logo')"
                  style="padding-top:2px;"
                ></span>
              </p>
            </template>
          </div>
        </section>

        <ExtraContent
          :descriptions="languageDescriptions" :next-session="nextSession" :supplier-name="supplierName"
          :spoken-languages="spokenLanguages" :documentations="documentations"
        />
        <EcoFriendly v-if="isEcoFriendlyProduct" :activity-name="localizedTitle" />
        <YoutubeContent v-if="youtubeVideos.length" :youtube-videos="youtubeVideos" />

        <GoogleRatingReviews v-if="ratingsPOJO" :ratings="ratingsPOJO" />
        <section v-if="!isFFVDistributor" id="fairguest_wrapper" class="normal_spacing" style="display:none;margin-block:var(--yb-spacing-2xl);" />
      </div>
    </div>

    <MapWidget v-if="productDetails" :navigation-links="navigationLinks" :map-data="mapWidgetData" />

    <section v-if="isFFVDistributor" id="fairguest_wrapper" class="normal_spacing" style="display:none;margin-block:var(--yb-spacing-2xl);" />

    <section
      v-if="crossSellingActivities.length"
      id="crosselling" class="normal_spacing crosselling_wrapper" :aria-label="t('aria.crosselling')"
      :style="crossSellingProductIds.length > 0 ? 'display:block;position:relative' : 'display:none;position:relative'"
    >
      <h2 id="crosselling_title" class="h2_title small_spacing">
        {{ t('content.other_activities') }}
      </h2>
      <Carousel
        style="min-height: 450px;" :items="crossSellingActivities" :min-item-width="260" :max-item-width="270"
        :item-width="270"
      >
        <template #item="{ item }">
          <TheCard
            style="height: 99%"
            :product="item"
            :responsive-row="false"
            :prefix-url="prefixUrl"
            :show-favorite="showFavorites"
            :with-slider="true"
            :has-large-viewport="desktopViewport"
          />
        </template>
      </Carousel>
    </section>
  </section>

  <section id="error" class="error_handler yb-preset" :style="[isError ? 'display:grid;' : 'display:none;']">
    <h3 class="h3_title normal_spacing">
      {{ t('error.data_unavailable') }}
    </h3>
    <button id="retry" type="button" class="error_retry" @click="forcePageRefresh">
      {{ t('cta.retry') }}
    </button>
  </section>
</template>

<style lang="css" scoped>
.h2_title {
  font-size: 25px;
  font-weight: var(--yb-text-bold);
  text-wrap: auto;
}

.h3_title {
  font-size: 18px;
  font-weight: var(--yb-text-bold);
}

.text_content {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-light);
  line-height: var(--yb-text-lineheight);
}

.text_content_bold {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-bold);
  line-height: var(--yb-text-lineheight);
}

.text_content_small {
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-light);
  line-height: var(--yb-text-lineheight);
}

.small_spacing {
  margin-bottom: var(--yb-spacing-sm);
}

.normal_spacing {
  margin-bottom: var(--yb-radius-base);
}

.details_component {
  max-width: min(100%, 1280);
  text-wrap: balance;
  margin: 0 auto;
  padding: var(--yb-radius-sm);
  scroll-behavior: smooth;

  @media screen and (width > 767px) {
    margin-bottom: var(--yb-radius-sm);
  }

  @media screen and (width > 1024px) {
    padding: 0;
  }

  .mobile_cta {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--yb-color-white);
    box-shadow: var(--yb-details-bottom-box-shadow);
    padding: var(--yb-radius-sm);
    z-index: 10;
    transition: transform 0.3s ease;

    &.hidden {
      transform: translateY(100%);
      pointer-events: none;
    }

    @media screen and (width > 1023px) {
      display: none;
    }

    .mobile_wrapper {
      display: flex;
      align-items: center;
      padding: var(--yb-spacing-sm);

      .price_wrapper {
        font-size: 24px;
        font-weight: 700;
        color: var(--yb-color-primary);

        .price_from {
          font-weight: var(--yb-text-bold);
          color: var(--yb-color-primary);
        }
      }
    }

    .cta_action {
      display: inline-block;
      appearance: none;
      text-decoration: none;
      background-color: var(--yb-color-primary);
      color: var(--yb-button-text-color);
      text-align: center;
      font-family: var(--yb-default-font), sans-serif;
      font-size: var(--yb-text-xl);
      font-weight: var(--yb-text-bold);
      border: none;
      cursor: pointer;
      padding-block: var(--yb-radius-base);
      padding-inline: 3.5rem;
      border-radius: var(--yb-radius-full);
      margin-bottom: var(--yb-radius-sm);
      margin-left: auto;
    }

    .cta_action:focus-visible {
      outline: 2px solid var(--yb-color-primary);
    }
  }

  .sticky {
    @media screen and (width >= 1024px) {
      position: sticky;
      top: var(--yb-radius-base);
    }
  }

  .desktop_cta {
    display: none;
    border-radius: var(--yb-card-radius);

    @media screen and (width > 1023px) {
      display: block;
      padding: var(--yb-spacing-xl);
      margin-top: 1.5rem;
      border: 1px solid var(--yb-color-mediumgray);
    }

    .desktop_price_wrapper {
      font-size: 24px;
      font-weight: 700;
      margin-top: 0.5rem;
      margin-bottom: 1.5rem;

      .desktop_price_from {
        font-weight: var(--yb-text-bold);
        color: var(--yb-color-primary);
      }
    }

    .provider {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: calc(var(--yb-spacing-sm) + var(--yb-radius-sm));

      .provider_content {
        display: flex;
        align-items: center;
        line-height: 110%;
        max-width: 24ch;
      }
    }

    .cta_action {
      display: inline-block;
      appearance: none;
      max-width: 350px;
      text-decoration: none;
      background-color: var(--yb-color-primary);
      color: var(--yb-button-text-color);
      width: 100%;
      text-align: center;
      font-size: var(--yb-text-xl);
      font-weight: var(--yb-text-bold);
      border: none;
      cursor: pointer;
      padding-block: var(--yb-radius-base);
      border-radius: var(--yb-radius-full);
      margin-inline: auto;
    }

    .cta_action:focus-visible {
      outline: 2px solid var(--yb-color-primary);
    }
  }

  .warning {
    background-color: #fefce8;
    border-radius: 4px;
    padding: 2rem;

    span {
      font-weight: 600;
    }
  }

  #widget_wrapper {
    width: 100%;
    min-height: 400px;
    background-color: var(--yb-color-white);
    margin-block: 0.25rem;
    border-radius: var(--yb-radius-sm);
    border: 1px solid transparent;
    transition: height 0.2s ease-out;
  }

  .main_container {
    @media screen and (width >= 1024px) {
      display: grid;
      grid-template-columns: 1fr 28%;
      gap: var(--yb-radius-base);
    }
  }

  .main_content {
    .provider_wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: calc(var(--yb-radius-sm) / 2);
    }

    .highlights_wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--yb-radius-sm);
    }

    @media screen and (width >= 1024px) {
      grid-column-start: 1;
      grid-row-start: 1;
    }
  }

  .side_content {
    @media screen and (width >= 1024px) {
      grid-column-start: 2;
      grid-row-start: 1;
    }
  }

  .provider_img {
    width: 30%;
    height: auto;
    margin-left: var(--yb-spacing-xs);
  }
}

.error_handler {
  place-items: center;
  min-height: 50vh;
  padding: var(--yb-radius-sm);
  text-align: center;
}

.error_retry {
  display: inline-block;
  appearance: none;
  width: 350px;
  background-color: var(--yb-color-primary);
  color: var(--yb-button-text-color);
  text-align: center;
  font-size: var(--yb-text-xl);
  font-weight: var(--yb-text-bold);
  border: none;
  cursor: pointer;
  padding-block: var(--yb-radius-sm);
  border-radius: 2.5rem;
  margin-bottom: var(--yb-radius-sm);
  margin-inline: auto;
}

.crosselling_wrapper {
  margin-block: var(--yb-spacing-2xl);
}

@media screen and (width < 1024px) {
  #crosselling {
    margin-bottom: 8rem;
  }
}
</style>
